import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import BasePage from '../../components/Layout/BasePage';
import useUrlTab from '../../hooks/useUrlTab';
import WarehouseDashboard from './WarehouseDashboard';
import './HomePage.css';
import './SupervisorHomePage.css'; /* Reuse the same navigation styles */

const WarehouseHomePage = ({ initialDetailTarget, onDetailConsumed }) => {
  const { t } = useTranslation('warehouse');
  const location = useLocation();
  // Inventory item to open ({ id, nonce }) and options for the target tab (e.g. stock filter)
  const [openItem, setOpenItem] = useState(null);
  const [navOptions, setNavOptions] = useState(null);
  const warehouseDashboardRef = useRef();

  // Handle detail target from App.js (/inventory/items/:id)
  useEffect(() => {
    if (!initialDetailTarget) return;
    if (initialDetailTarget.type === 'inventoryItem') {
      setActiveTab('inventory-mgmt', 'inventory');
      setOpenItem({ id: initialDetailTarget.id, nonce: Date.now() });
    }
    if (onDetailConsumed) onDetailConsumed();
  }, [initialDetailTarget]);

  // Back to /inventory (F6, header) closes an item opened through its URL
  useEffect(() => {
    if (openItem && !location.pathname.startsWith('/inventory/items/')) {
      setOpenItem(null);
    }
  }, [location.pathname]);

  // Overview cards and quick links switch tabs here (this page controls the dashboard's tabs)
  const handleNavigate = (tab, subTab, options = null) => {
    setActiveTab(tab, subTab);
    setNavOptions(options);
  };

  // Tab configuration - restored from original WarehouseDashboard + new Fulfillment tab
  const tabConfig = {
    'overview': {
      name: t('navigation.tabs.overview'),
      icon: '📊',
      subtabs: {
        'dashboard': { name: t('navigation.subtabs.dashboard'), icon: '🏠' }
      }
    },
    'inventory-mgmt': {
      name: t('navigation.tabs.inventoryManagement'),
      icon: '📦',
      subtabs: {
        'inventory': { name: t('navigation.subtabs.materialsInventory'), icon: '📊' },
        'storage': { name: t('navigation.subtabs.inventoryTransfers'), icon: '🔄' },
        'locations': { name: t('navigation.subtabs.warehouseLocations'), icon: '📍' },
        'cycle-counts': { name: t('navigation.subtabs.cycleCounts', 'Cycle Counts'), icon: '🔢' },
        'formulations': { name: t('navigation.subtabs.formulations', 'Ink Formulations'), icon: '🧪' },
        'extrusions': { name: t('navigation.subtabs.extrusions', 'Plastic Extrusion'), icon: '🏭' }
      }
    },
    'receiving-ops': {
      name: t('navigation.tabs.receivingOperations'),
      icon: '📥',
      subtabs: {
        'incoming': { name: t('navigation.subtabs.incomingDeliveries'), icon: '📦' },
        'receipt': { name: t('navigation.subtabs.goodsReceipt'), icon: '📋' },
        'quality': { name: t('navigation.subtabs.qualityCheck'), icon: '🔍' },
        'finished-goods': { name: t('navigation.subtabs.finishedGoodsReceipt', 'Finished Goods Receipt'), icon: '🏭' }
      }
    },
    'shipping-ops': {
      name: t('navigation.tabs.shippingOperations'),
      icon: '📤',
      subtabs: {
        'picking': { name: t('navigation.subtabs.pickLists'), icon: '📝' },
        'packing': { name: t('navigation.subtabs.packing'), icon: '📦' },
        'shipping': { name: t('navigation.subtabs.salesOrderShipping'), icon: '🚢' }
      }
    },
    'returns-reqs': {
      name: t('navigation.tabs.returnsRequisitions'),
      icon: '🔄',
      subtabs: {
        'requisitions': { name: t('navigation.subtabs.materialRequisitions'), icon: '📋' },
        'returns': { name: t('navigation.subtabs.supplierReturns'), icon: '↩️' },
        'material-returns': { name: t('navigation.subtabs.materialReturns'), icon: '📥' }
      }
    }
  };

  // Active tab and sub-tab in the URL (?tab=…&sub=…), remembered like the other modules
  const { activeTab, activeSubTab, setActiveTab } = useUrlTab(
    'inventory', Object.keys(tabConfig), 'overview', (tabId) => Object.keys(tabConfig[tabId]?.subtabs || {})
  );

  const handleRefresh = () => {
    // Call refresh method on WarehouseDashboard if available
    if (warehouseDashboardRef.current?.refresh) {
      warehouseDashboardRef.current.refresh();
    }
  };

  const renderTabContent = () => {
    return (
      <WarehouseDashboard
        ref={warehouseDashboardRef}
        externalTabControl={true}
        externalActiveTab={activeTab}
        externalActiveSubTab={activeSubTab}
        onExternalNavigate={handleNavigate}
        openItem={openItem}
        onItemClosed={() => setOpenItem(null)}
        navOptions={navOptions}
      />
    );
  };


  return (
    <BasePage title={t('navigation.title')} className="supervisor-home-page">
      <div className="supervisor-navigation">
        {/* Main Tabs */}
        <div className="main-tabs">
          {Object.entries(tabConfig).map(([tabId, tab]) => (
            <button
              key={tabId}
              className={`main-tab ${activeTab === tabId ? 'active' : ''}`}
              onClick={() => {
                // First subtab of the new main tab
                setActiveTab(tabId);
                setNavOptions(null);
              }}
            >
              <span className="tab-icon">{tab.icon}</span>
              <span className="tab-label">{tab.name}</span>
            </button>
          ))}
        </div>

        {/* Sub Tabs */}
        <div className="sub-tabs">
          <div className="subtabs-left">
            {tabConfig[activeTab] && Object.entries(tabConfig[activeTab].subtabs).map(([subTabId, subTab]) => (
              <button
                key={subTabId}
                className={`sub-tab ${activeSubTab === subTabId ? 'active' : ''}`}
                onClick={() => { setActiveTab(activeTab, subTabId); setNavOptions(null); }}
              >
                <span className="subtab-icon">{subTab.icon}</span>
                <span className="subtab-label">{subTab.name}</span>
              </button>
            ))}
          </div>
          <button
            className="btn-refresh-subtab"
            onClick={handleRefresh}
          >
            🔄 {t('common.buttons.refresh')}
          </button>
        </div>
      </div>

      <div className="supervisor-content"> {/* Reuse supervisor styles */}
        {renderTabContent()}
      </div>
    </BasePage>
  );
};

export default WarehouseHomePage;